import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2, FiX, FiMinusCircle } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import {
  variantDisplay,
  partDocById,
  computeAssemblyPackagingWeight,
  formatEquivalentWeight,
  labelWithUnit,
  partAssignedUnitSymbol,
} from "../PartMachines/partMachinesUtils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const variantPartLabel = (p) => {
  const v = variantDisplay(p?.variantId);
  if (v) return `${p.code || "—"} — ${v}`;
  return `${p.code || "—"} — ${p.name || "—"}`;
};

const resolveMaterialRow = (partDoc) => {
  const id = String(partDoc?._id || partDoc || "");
  const mainId = String(
    partDoc?.mainPartId?._id || partDoc?.mainPartId || "",
  );
  if (mainId && mainId !== "undefined") {
    return { mainPartId: mainId, partId: id };
  }
  return { mainPartId: id, partId: id };
};

const sectionStyle = {
  border: "1px solid #e2e5e8",
  borderRadius: 8,
  padding: "12px 14px",
  background: "var(--m-card-background-color, #fff)",
};

const sectionTitleStyle = {
  fontSize: "0.8rem",
  fontWeight: 700,
  color: "#334155",
  marginBottom: 10,
};

const sectionTitle = (step, title) => (
  <div style={sectionTitleStyle}>
    <span style={{ color: "#94a3b8", marginRight: 6 }}>{step}.</span>
    {title}
  </div>
);

const tableColumns = [
  {
    name: "Model Kodu",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(a?.deviceModel?.code || "").localeCompare(
        String(b?.deviceModel?.code || ""),
      ),
  },
  {
    name: "Model Adı",
    dataType: "text",
    id: 2,
    customSorter: (a, b) =>
      String(a?.deviceModel?.name || "").localeCompare(
        String(b?.deviceModel?.name || ""),
      ),
  },
  { name: "Girdi", id: 3, sortable: false },
  { name: "Ağırlık", id: 6, sortable: false },
  {
    name: "Durum",
    dataType: "text",
    id: 4,
    customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive),
  },
  { name: "İşlemler", id: 5, sortable: false },
];

const emptyForm = () => ({
  deviceModel: "",
  inputMaterials: [],
  isActive: true,
});

const PartAssemblies = () => {
  useTitle("Rovion MES - Parça / Montaj");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/part-assemblies",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("part-assemblies-list", tableColumns);

  const [records, setRecords] = useState([]);
  const [parts, setParts] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });
  const [variantCache, setVariantCache] = useState({});
  const [variantLoading, setVariantLoading] = useState({});
  const [wastageModal, setWastageModal] = useState({
    open: false,
    rowIndex: null,
    draft: "0",
  });
  const [wastageBulkModal, setWastageBulkModal] = useState({
    open: false,
    draft: "0",
    targetIds: [],
    unitSymbol: "",
  });

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-assemblies`,
        authHeaders(),
      );
      setRecords(data.partAssemblies || []);
    } catch {
      addToast("Kayıtlar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  const fetchParts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/parts`, authHeaders());
      setParts(data.parts || []);
    } catch {}
  }, [authHeaders]);

  const fetchModels = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/device-models`,
        authHeaders(),
      );
      setModels(Array.isArray(data) ? data : data.models || data.deviceModels || []);
    } catch {}
  }, [authHeaders]);

  useEffect(() => {
    fetchRecords();
    fetchParts();
    fetchModels();
  }, [fetchRecords, fetchParts, fetchModels]);

  const openCreate = () => {
    setFormData(emptyForm());
    setEditingItem(null);
    setSidebarOpened(true);
  };

  const fetchVariantsForMain = useCallback(
    async (mainPartId) => {
      const mid = String(mainPartId || "");
      if (!mid) return [];
      setVariantLoading((s) => ({ ...s, [mid]: true }));
      try {
        const { data } = await axios.get(
          `${API_URL}/api/part-assemblies/main-parts/${mid}/variants`,
          authHeaders(),
        );
        const items = data.variants || [];
        setVariantCache((s) => ({ ...s, [mid]: items }));
        return items;
      } catch {
        return [];
      } finally {
        setVariantLoading((s) => ({ ...s, [mid]: false }));
      }
    },
    [authHeaders],
  );

  const openEdit = async (item) => {
    const rows = (item.inputMaterials || []).map((m) => {
      const doc = m.partId;
      const { mainPartId, partId } = resolveMaterialRow(doc);
      return {
        mainPartId,
        partId,
        consumptionRatio: String(m.consumptionRatio ?? 1),
        wastage:
          m.wastage !== undefined && m.wastage !== null
            ? String(m.wastage)
            : "0",
        weightUnit: m.weightUnit ? String(m.weightUnit) : "",
      };
    });
    setFormData({
      deviceModel: String(item.deviceModel?._id || item.deviceModel || ""),
      inputMaterials: rows,
      isActive: item.isActive !== false,
    });
    setEditingItem(item);
    setSidebarOpened(true);
    await Promise.all(
      [...new Set(rows.map((r) => r.mainPartId).filter(Boolean))].map(
        fetchVariantsForMain,
      ),
    );
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingItem(null);
    setFormData(emptyForm());
    setVariantCache({});
    setVariantLoading({});
  };

  const mainPartById = (id) =>
    parts.find((p) => String(p._id) === String(id));

  const variantsForMain = (mainPartId) =>
    variantCache[String(mainPartId || "")] ?? null;

  const mainPartHasVariants = (mainPartId) => {
    const mid = String(mainPartId || "");
    if (!mid) return false;
    const cached = variantsForMain(mid);
    if (cached !== null) return cached.length > 0;
    const mp = mainPartById(mid);
    return Number(mp?.variantCount) > 0;
  };

  const effectivePartId = (row) => {
    const mid = String(row.mainPartId || "");
    if (!mid) return "";
    if (mainPartHasVariants(mid)) return String(row.partId || "");
    return mid;
  };

  const partDocForInputRow = (row) => {
    const pid = effectivePartId(row);
    if (!pid) return null;
    const mid = String(row.mainPartId || "");
    return (
      partDocById(pid, variantCache[mid] || [], parts) ||
      mainPartById(mid) ||
      null
    );
  };

  const inputRowLabel = (row, rowIndex = null) => {
    const doc = partDocForInputRow(row);
    if (doc) return variantPartLabel(doc);
    if (rowIndex != null) return `Satır ${rowIndex + 1}`;
    return "—";
  };

  const unitForInputRow = (row, partDoc) => {
    const stored = String(row?.weightUnit || "").trim();
    if (stored) return stored;
    return partAssignedUnitSymbol(partDoc, mainPartById);
  };

  const editingModelId = editingItem
    ? String(editingItem.deviceModel?._id || editingItem.deviceModel || "")
    : "";
  const modelIdsWithRecords = new Set(
    records.map((r) => String(r.deviceModel?._id || r.deviceModel || "")),
  );
  const modelOptions = models
    .filter((m) => {
      const id = String(m._id);
      return id === editingModelId || !modelIdsWithRecords.has(id);
    })
    .map((m) => ({
      value: String(m._id),
      label: `${m.code} — ${m.name}`,
    }));
  const hasCreatableModel = models.some(
    (m) => !modelIdsWithRecords.has(String(m._id)),
  );
  const getInputMainPartOptionsForRow = (rowIndex) => {
    const usedElsewhere = new Set(
      formData.inputMaterials
        .map((m, idx) => (idx !== rowIndex ? effectivePartId(m) : null))
        .filter(Boolean),
    );
    return parts
      .filter((p) => {
        const id = String(p._id);
        const variantCount = Number(p.variantCount) || 0;
        if (variantCount > 0) return true;
        return !usedElsewhere.has(id);
      })
      .map((p) => ({
        value: String(p._id),
        label: `${p.code} — ${p.name}`,
      }));
  };

  const getVariantOptionsForRow = (rowIndex, row) => {
    const usedElsewhere = new Set(
      formData.inputMaterials
        .map((m, idx) => (idx !== rowIndex ? effectivePartId(m) : null))
        .filter(Boolean),
    );
    const items = variantCache[String(row.mainPartId)] || [];
    return items
      .filter((v) => !usedElsewhere.has(String(v._id)))
      .map((v) => ({
        value: String(v._id),
        label: variantPartLabel(v),
      }));
  };

  const addInputMaterial = () =>
    setFormData((s) => ({
      ...s,
      inputMaterials: [
        ...s.inputMaterials,
        { mainPartId: "", partId: "", consumptionRatio: "1", wastage: "0" },
      ],
    }));

  const removeInputMaterial = (i) =>
    setFormData((s) => {
      const next = [...s.inputMaterials];
      next.splice(i, 1);
      return { ...s, inputMaterials: next };
    });

  const updateInputMaterial = (i, field, value) =>
    setFormData((s) => {
      const next = [...s.inputMaterials];
      next[i] = { ...next[i], [field]: value };
      if (field === "partId") {
        const doc = partDocForInputRow(next[i]);
        next[i].weightUnit = partAssignedUnitSymbol(doc, mainPartById);
      }
      return { ...s, inputMaterials: next };
    });

  const buildPayloadMaterials = () =>
    formData.inputMaterials
      .map((m) => ({
        partId: effectivePartId(m),
        consumptionRatio: Math.max(0, Number(m.consumptionRatio) || 1),
        wastage: Math.max(0, Number(m.wastage) || 0),
      }))
      .filter((m) => m.partId);

  const packagingWeightPreview = computeAssemblyPackagingWeight(
    buildPayloadMaterials(),
    parts,
  );

  const wastageModalRowUnit = (() => {
    const idx = wastageModal.rowIndex;
    if (idx == null) return "";
    const row = formData.inputMaterials[idx];
    if (!row) return "";
    const doc = partDocForInputRow(row);
    return unitForInputRow(row, doc);
  })();

  const formatWastageDisplay = (value, unit = "") => {
    const n = Number(value);
    if (!String(value ?? "").trim() || Number.isNaN(n) || n <= 0) return "";
    const u = String(unit || "").trim();
    return u ? formatEquivalentWeight(n, u) : String(value);
  };

  const bulkWastageInputOptions = () =>
    formData.inputMaterials
      .map((row, i) => {
        const pid = effectivePartId(row);
        if (!pid) return null;
        return {
          value: String(i),
          label: inputRowLabel(row, i),
        };
      })
      .filter(Boolean);

  const resolveBulkWastageUnit = (targetIds) => {
    const ids = (targetIds || []).map(String).filter(Boolean);
    if (!ids.length) return "";
    const units = ids
      .map((id) => {
        const i = Number(id);
        const row = formData.inputMaterials[i];
        if (!row) return "";
        return unitForInputRow(row, partDocForInputRow(row));
      })
      .filter(Boolean);
    const unique = [...new Set(units)];
    return unique.length === 1 ? unique[0] : "";
  };

  const applyBulkWastage = (targetIds, wastageValue) => {
    const idxs = (targetIds || [])
      .map((id) => Number(id))
      .filter((n) => !Number.isNaN(n) && n >= 0);
    if (!idxs.length) return;
    const value = String(wastageValue ?? "0");
    setFormData((s) => {
      const next = [...s.inputMaterials];
      for (const i of idxs) {
        if (!next[i]) continue;
        const row = next[i];
        const u = unitForInputRow(row, partDocForInputRow(row));
        next[i] = {
          ...row,
          wastage: value,
          ...(u ? { weightUnit: u } : {}),
        };
      }
      return { ...s, inputMaterials: next };
    });
  };

  const updateInputMainPart = async (i, mainPartId) => {
    const v = String(mainPartId || "");
    setFormData((s) => {
      const next = [...s.inputMaterials];
      next[i] = { ...next[i], mainPartId: v, partId: "" };
      return { ...s, inputMaterials: next };
    });
    if (!v) return;
    const items = await fetchVariantsForMain(v);
    if (items.length === 0) {
      setFormData((s) => {
        const next = [...s.inputMaterials];
        const row = { ...next[i], mainPartId: v, partId: v };
        const doc = partDocForInputRow(row);
        row.weightUnit = partAssignedUnitSymbol(doc, mainPartById);
        next[i] = row;
        return { ...s, inputMaterials: next };
      });
    }
  };

  const handleSave = async () => {
    if (!formData.deviceModel) {
      addToast("Model seçiniz", "danger");
      return;
    }
    const missingMain = formData.inputMaterials.some((m) => !m.mainPartId);
    if (missingMain) {
      addToast("Tüm satırlarda ana parça seçin", "danger");
      return;
    }
    const missingVariant = formData.inputMaterials.some(
      (m) => mainPartHasVariants(m.mainPartId) && !m.partId,
    );
    if (missingVariant) {
      addToast("Varyantı olan parçalarda varyant seçin", "danger");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        deviceModel: formData.deviceModel,
        inputMaterials: buildPayloadMaterials(),
        isActive: formData.isActive,
      };
      if (editingItem) {
        await axios.put(
          `${API_URL}/api/part-assemblies/${editingItem._id}`,
          payload,
          authHeaders(),
        );
        addToast("Güncellendi", "success");
      } else {
        const { data } = await axios.post(
          `${API_URL}/api/part-assemblies`,
          payload,
          authHeaders(),
        );
        addToast(data?.message || "Kaydedildi", "success");
      }
      closeSidebar();
      fetchRecords();
    } catch (err) {
      const { data } = err?.response || {};
      addToast(data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/part-assemblies/${id}`, authHeaders());
      addToast("Silindi", "success");
      fetchRecords();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  return (
    <AppPage
      title="Parça / Montaj"
      subtitle="Paketleme tüketimi (montaj ağacındaki model bağlantısından ayrıdır)"
      actions={
        canWrite && (
          <Button
            appearance="primary-animated"
            onClick={openCreate}
            icon={<FiPlus />}
            disabled={!hasCreatableModel}
          >
            Yeni Kayıt
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, id: null })}
        width="small"
      >
        <ModalHeader>Kaydı Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page">
            Bu kaydı silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={() => handleDelete(confirmModal.id)}
            >
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() => setConfirmModal({ isOpen: false, id: null })}
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={wastageModal.open}
        onClose={() =>
          setWastageModal({ open: false, rowIndex: null, draft: "0" })
        }
        width="small"
      >
        <ModalHeader>Fire</ModalHeader>
        <ModalBody>
          <p className="text-muted small mb-2">
            Fire, bu girdi satırının ağırlığından düşülür.
          </p>
          <Label>{labelWithUnit("Fire miktarı", wastageModalRowUnit)}</Label>
          <div className="d-flex align-items-center gap-1">
            <Input
              type="number"
              min="0"
              step="any"
              value={wastageModal.draft}
              onChange={(v) => setWastageModal((s) => ({ ...s, draft: v }))}
            />
            {wastageModalRowUnit ? (
              <span
                style={{
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  whiteSpace: "nowrap",
                }}
              >
                {wastageModalRowUnit}
              </span>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              onClick={() => {
                const idx = wastageModal.rowIndex;
                if (idx != null) {
                  const row = formData.inputMaterials[idx];
                  updateInputMaterial(idx, "wastage", wastageModal.draft);
                  const u = unitForInputRow(row, partDocForInputRow(row));
                  if (u) updateInputMaterial(idx, "weightUnit", u);
                }
                setWastageModal({ open: false, rowIndex: null, draft: "0" });
              }}
            >
              Kaydet
            </Button>
            <Button
              appearance="subtle"
              onClick={() =>
                setWastageModal({ open: false, rowIndex: null, draft: "0" })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={wastageBulkModal.open}
        onClose={() =>
          setWastageBulkModal({
            open: false,
            draft: "0",
            targetIds: [],
            unitSymbol: "",
          })
        }
        width="medium"
      >
        <ModalHeader>Toplu Fire</ModalHeader>
        <ModalBody>
          <Select
            className="mb-3"
            label="Girdi parçaları"
            placeholder="— Parça seçin —"
            options={bulkWastageInputOptions()}
            selected={wastageBulkModal.targetIds}
            searchInDropdown
            searchPlaceholder="Parça ara…"
            onSelected={(vals) => {
              const targetIds = (vals || []).map((v) => String(v.value));
              setWastageBulkModal((s) => ({
                ...s,
                targetIds,
                unitSymbol: resolveBulkWastageUnit(targetIds),
              }));
            }}
          />
          <div className="d-flex gap-2 mb-3">
            <Button
              appearance="primary-subtle"
              onClick={() => {
                const targetIds = bulkWastageInputOptions().map((o) => o.value);
                setWastageBulkModal((s) => ({
                  ...s,
                  targetIds,
                  unitSymbol: resolveBulkWastageUnit(targetIds),
                }));
              }}
            >
              Tümünü seç
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setWastageBulkModal((s) => ({
                  ...s,
                  targetIds: [],
                  unitSymbol: "",
                }))
              }
            >
              Seçimi temizle
            </Button>
          </div>
          {!wastageBulkModal.unitSymbol &&
          wastageBulkModal.targetIds.length > 1 ? (
            <p className="text-muted small mb-2">
              Seçili satırlar farklı birimlerde; aynı miktar her satırın kendi
              birimine (kg, lt, …) uygulanır.
            </p>
          ) : null}
          <Label>
            Fire miktarı
            {wastageBulkModal.unitSymbol
              ? ` (${wastageBulkModal.unitSymbol})`
              : ""}
          </Label>
          <div
            className="d-flex align-items-center gap-1"
            style={{ maxWidth: 280 }}
          >
            <Input
              type="number"
              min="0"
              step="any"
              value={wastageBulkModal.draft}
              onChange={(v) => setWastageBulkModal((s) => ({ ...s, draft: v }))}
            />
            {wastageBulkModal.unitSymbol ? (
              <span
                style={{
                  fontSize: "0.8125rem",
                  color: "#64748b",
                  whiteSpace: "nowrap",
                }}
              >
                {wastageBulkModal.unitSymbol}
              </span>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="primary-animated"
              disabled={!wastageBulkModal.targetIds.length}
              onClick={() => {
                applyBulkWastage(
                  wastageBulkModal.targetIds,
                  wastageBulkModal.draft,
                );
                addToast(
                  `${wastageBulkModal.targetIds.length} satıra fire uygulandı`,
                  "success",
                );
                setWastageBulkModal({
                  open: false,
                  draft: "0",
                  targetIds: [],
                  unitSymbol: "",
                });
              }}
            >
              Uygula
            </Button>
            <Button
              appearance="danger-subtle"
              onClick={() =>
                setWastageBulkModal({
                  open: false,
                  draft: "0",
                  targetIds: [],
                  unitSymbol: "",
                })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          tableId="part-assemblies-list"
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {records.map((r) => (
            <Tr key={r._id} data={r}>
              {visibleColumns.includes(1) && (
              <Td>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                    background: "var(--m-badge-default-subtle-bg, #f1f5f9)",
                    padding: "2px 6px",
                    borderRadius: 4,
                  }}
                >
                  {r.deviceModel?.code || "—"}
                </span>
              </Td>
              )}
              {visibleColumns.includes(2) && (
              <Td>
                <strong style={{ fontSize: "0.875rem" }}>
                  {r.deviceModel?.name || "—"}
                </strong>
              </Td>
              )}
              {visibleColumns.includes(3) && (
              <Td>
                {r.inputMaterials && r.inputMaterials.length > 0 ? (
                  <div style={{ fontSize: "0.78rem" }}>
                    {r.inputMaterials.map((m, i) => {
                      const doc = m.partId;
                      const vLabel = variantDisplay(doc?.variantId);
                      const mainCode =
                        doc?.mainPartId?.code || doc?.code || "—";
                      return (
                        <div key={i} style={{ color: "#475569" }}>
                          <span style={{ fontWeight: 600 }}>
                            {vLabel ? `${mainCode} (${vLabel})` : mainCode}
                          </span>
                          <span className="text-muted ms-1">
                            × {m.consumptionRatio}
                            {Number(m.wastage) > 0
                              ? ` · fire ${formatWastageDisplay(
                                  m.wastage,
                                  String(m.weightUnit || "").trim() ||
                                    partAssignedUnitSymbol(doc, undefined),
                                ) || m.wastage}`
                              : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </Td>
              )}
              {visibleColumns.includes(6) && (
              <Td>
                {r.outputWeight != null && r.weightUnit ? (
                  <span
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      color: "#475569",
                    }}
                  >
                    {formatEquivalentWeight(r.outputWeight, r.weightUnit)}
                  </span>
                ) : (
                  <span className="text-muted small">—</span>
                )}
              </Td>
              )}
              {visibleColumns.includes(4) && (
              <Td>
                {r.isActive ? (
                  <Lozenge appearance="success-subtle" isBold>
                    Aktif
                  </Lozenge>
                ) : (
                  <Lozenge appearance="default-subtle" isBold>
                    Pasif
                  </Lozenge>
                )}
              </Td>
              )}
              {visibleColumns.includes(5) && (
              <Td>
                {canWrite && (
                  <div className="d-flex gap-2">
                    <IconButton
                      icon={<FiEdit2 />}
                      label="Düzenle"
                      appearance="subtle"
                      shape="circle"
                      onClick={() => openEdit(r)}
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      label="Sil"
                      appearance="subtle"
                      shape="circle"
                      onClick={() =>
                        setConfirmModal({ isOpen: true, id: r._id })
                      }
                    />
                  </div>
                )}
              </Td>
              )}
            </Tr>
          ))}
        </Table>
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingItem ? "Kayıt Düzenle" : "Yeni Kayıt"}
        onClose={closeSidebar}
      >
        <div
          className="d-flex flex-column gap-3"
          style={{ paddingBottom: 16 }}
        >
          <div style={sectionStyle}>
            {sectionTitle(1, "Ürün modeli")}
            <Select
              className="mb-0"
              label="Ürün modeli"
              required
              unique
              options={modelOptions}
              selected={
                formData.deviceModel ? [String(formData.deviceModel)] : []
              }
              onSelected={(values) => {
                const v = values?.[0]?.value || "";
                setFormData((s) => ({ ...s, deviceModel: v }));
              }}
              placeholder="— Seçin —"
            />
          </div>

          <div style={sectionStyle}>
            <div
              className="d-flex flex-wrap align-items-center justify-content-between gap-2"
              style={{ marginBottom: 10 }}
            >
              <div style={sectionTitleStyle}>
                <span style={{ color: "#94a3b8", marginRight: 6 }}>2.</span>
                Montajda tüketilecek parçalar
              </div>
              <div className="d-flex align-items-center gap-2">
                {packagingWeightPreview.ok ? (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "#475569",
                    }}
                    title="Hesaplanan paketleme ağırlığı"
                  >
                    {formatEquivalentWeight(
                      packagingWeightPreview.net,
                      packagingWeightPreview.unitSymbol,
                    )}
                  </span>
                ) : null}
                <Button
                  appearance="primary-subtle"
                  icon={<FiMinusCircle size={14} />}
                  disabled={!formData.inputMaterials.length}
                  onClick={async () => {
                    const mains = [
                      ...new Set(
                        formData.inputMaterials
                          .map((m) => String(m.mainPartId || ""))
                          .filter((id) => id && mainPartHasVariants(id)),
                      ),
                    ];
                    await Promise.all(mains.map((id) => fetchVariantsForMain(id)));
                    const targetIds = bulkWastageInputOptions().map((o) => o.value);
                    setWastageBulkModal({
                      open: true,
                      draft: "0",
                      targetIds,
                      unitSymbol: resolveBulkWastageUnit(targetIds),
                    });
                  }}
                >
                  Toplu Fire
                </Button>
              </div>
            </div>
            {formData.inputMaterials.length > 0 && (() => {
              const anyVariantCol = formData.inputMaterials.some(
                (m) => m.mainPartId && mainPartHasVariants(m.mainPartId),
              );
              return (
              <div
                className="d-flex gap-2 mb-1 px-1"
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#64748b",
                }}
              >
                <div style={{ flex: anyVariantCol ? 1.8 : 3 }}>Parça</div>
                {anyVariantCol && <div style={{ flex: 1.8 }}>Varyant</div>}
                <div style={{ flex: "0 0 64px", width: 64 }}>Adet</div>
                <div style={{ flex: "0 0 72px", width: 72, minWidth: 72 }}>
                  Fire
                </div>
                <div style={{ width: 36 }} />
              </div>
              );
            })()}
            {formData.inputMaterials.map((row, i) => {
              const hasVariants = mainPartHasVariants(row.mainPartId);
              const showVariantSelect = row.mainPartId && hasVariants;
              const anyVariantCol = formData.inputMaterials.some(
                (m) => m.mainPartId && mainPartHasVariants(m.mainPartId),
              );
              const rowDoc = partDocForInputRow(row);
              const rowUnit = unitForInputRow(row, rowDoc);
              const mainFlex = showVariantSelect
                ? 1.8
                : anyVariantCol
                  ? 3.6
                  : 3;
              return (
              <div key={i} className="d-flex align-items-end gap-2 mb-2">
                <div style={{ flex: mainFlex }}>
                  <Select
                    className="mb-0"
                    unique
                    searchInDropdown
                    searchPlaceholder="Ara (kod veya ad)..."
                    options={getInputMainPartOptionsForRow(i)}
                    selected={
                      row.mainPartId ? [String(row.mainPartId)] : []
                    }
                    onSelected={(values) => {
                      const v = values?.[0]?.value || "";
                      updateInputMainPart(i, v);
                    }}
                    placeholder="— Parça —"
                  />
                </div>
                {showVariantSelect && (
                <div style={{ flex: 1.8 }}>
                    <Select
                      className="mb-0"
                      unique
                      searchInDropdown
                      searchPlaceholder="Ara..."
                      options={getVariantOptionsForRow(i, row)}
                      selected={row.partId ? [String(row.partId)] : []}
                      onSelected={(values) => {
                        const v = values?.[0]?.value || "";
                        updateInputMaterial(i, "partId", v);
                      }}
                      placeholder={
                        variantLoading[row.mainPartId]
                          ? "Yükleniyor..."
                          : "— Varyant —"
                      }
                      disabled={!!variantLoading[row.mainPartId]}
                    />
                </div>
                )}
                <div style={{ flex: "0 0 64px", width: 64, maxWidth: 64 }}>
                  <Input
                    type="number"
                    min="0"
                    step="0.001"
                    value={row.consumptionRatio}
                    onChange={(v) =>
                      updateInputMaterial(i, "consumptionRatio", v)
                    }
                    placeholder="1"
                    style={{ fontSize: "0.8125rem" }}
                  />
                </div>
                <div
                  style={{
                    flex: "0 0 72px",
                    width: 72,
                    minWidth: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 2,
                    marginBottom: 2,
                  }}
                >
                  {Number(row.wastage) > 0 ? (
                    <span
                      title={rowUnit ? `Fire (${rowUnit})` : "Fire"}
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: "#b45309",
                        whiteSpace: "nowrap",
                        lineHeight: 1.1,
                      }}
                    >
                      {formatWastageDisplay(row.wastage, rowUnit) ||
                        (rowUnit
                          ? `${row.wastage} ${rowUnit}`
                          : String(row.wastage))}
                    </span>
                  ) : rowUnit ? (
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.6rem", whiteSpace: "nowrap" }}
                      title="Fire birimi"
                    >
                      {rowUnit}
                    </span>
                  ) : null}
                  <IconButton
                    icon={<FiMinusCircle size={14} />}
                    label="Fire"
                    appearance="subtle"
                    shape="circle"
                    onClick={() =>
                      setWastageModal({
                        open: true,
                        rowIndex: i,
                        draft: String(row.wastage ?? "0"),
                      })
                    }
                  />
                </div>
                <IconButton
                  icon={<FiX />}
                  label="Kaldır"
                  appearance="subtle"
                  shape="circle"
                  onClick={() => removeInputMaterial(i)}
                  style={{ marginBottom: 2 }}
                />
              </div>
            );
            })}
            <Button
              appearance="primary-subtle"
              onClick={addInputMaterial}
              icon={<FiPlus />}
              className="mt-1"
            >
              Parça ekle
            </Button>
          </div>

          <div style={sectionStyle}>
            {sectionTitle(3, "Durum")}
            <Select
              className="mb-0"
              label="Kayıt durumu"
              unique
              options={[
                { value: "1", label: "Aktif" },
                { value: "0", label: "Pasif" },
              ]}
              selected={[formData.isActive ? "1" : "0"]}
              onSelected={(values) => {
                const v = values?.[0]?.value === "0" ? false : true;
                setFormData((s) => ({ ...s, isActive: v }));
              }}
              placeholder="Durum seçin"
            />
          </div>

          <div className="pt-1 pb-1">
            <Button
              appearance="primary-animated"
              onClick={handleSave}
              disabled={saving || !formData.deviceModel}
              icon={editingItem ? <FiEdit2 /> : <FiPlus />}
              className="w-100"
            >
              {saving
                ? "Kaydediliyor..."
                : editingItem
                  ? "Güncelle"
                  : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(PartAssemblies);
